/** Deux logos : pastille barre (rond) + en-tête des pièces (libre, cadre fixe). */
import { useEffect, useRef, useState } from "react";
import { facturxFetch, facturxUpload } from "../../api/client";
import { CompanyMark } from "../../components/CompanyMark";
import { ConfirmModal } from "../../components/ConfirmModal";
import { companyLetterheadUrl } from "../../company/logoUrl";
import type { Company } from "../../types";
import { CoffreLogoPicker } from "./CoffreLogoPicker";

type LogoKind = "mark" | "letterhead";

type CompanyLogosProps = {
  company: Company;
  owner: boolean;
  busy: boolean;
  setBusy: (v: boolean) => void;
  onError: (msg: string) => void;
  onOk: (msg: string) => void;
  onSaved: () => Promise<void>;
};

export function CompanyLogos({ company, owner, busy, setBusy, onError, onOk, onSaved }: CompanyLogosProps) {
  const markRef = useRef<HTMLInputElement>(null);
  const headRef = useRef<HTMLInputElement>(null);
  const [coffreKind, setCoffreKind] = useState<LogoKind | null>(null);
  const [pending, setPending] = useState<LogoKind | null>(null);
  const [headBroken, setHeadBroken] = useState(false);
  useEffect(() => {
    setHeadBroken(false);
  }, [company.id, company.letterhead_logo_path, company.updated_at]);
  const hasHead = Boolean(company.letterhead_logo_path) && !headBroken;
  const hasMark = Boolean(company.logo_path);

  async function upload(kind: LogoKind, file: File | undefined) {
    if (!file) return;
    setBusy(true);
    onError("");
    try {
      const body = new FormData();
      body.set("company_id", String(company.id));
      body.set("kind", kind);
      body.set("logo", file);
      await facturxUpload("company-logo.php", body);
      await onSaved();
      if (kind === "letterhead") setHeadBroken(false);
      onOk(kind === "letterhead" ? "Logo d’en-tête enregistré." : "Logo de profil enregistré.");
    } catch (e) {
      onError(e instanceof Error ? e.message : "Erreur");
      throw e;
    } finally {
      setBusy(false);
    }
  }

  async function remove(kind: LogoKind) {
    setBusy(true);
    onError("");
    try {
      await facturxFetch("company-logo.php", {
        method: "DELETE",
        body: JSON.stringify({ company_id: company.id, kind }),
      });
      await onSaved();
      setPending(null);
      onOk(kind === "letterhead" ? "En-tête retiré." : "Logo de profil retiré.");
    } catch (e) {
      onError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fx-co-logos">
      <div className="fx-co-logo">
        <CompanyMark company={company} />
        <div>
          <strong>Profil</strong>
          <em>Rond dans la barre · JPEG, PNG ou WebP · 1,5 Mo</em>
          <div className="fx-co-logo__acts">
            <button type="button" disabled={busy || !owner} onClick={() => markRef.current?.click()}>
              Téléverser
            </button>
            <button type="button" disabled={busy || !owner} onClick={() => setCoffreKind("mark")}>
              Depuis le Coffre
            </button>
            {hasMark ? (
              <button type="button" disabled={busy || !owner} onClick={() => setPending("mark")}>
                Retirer
              </button>
            ) : null}
          </div>
        </div>
        <input
          ref={markRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => {
            void upload("mark", e.target.files?.[0]).catch(() => undefined);
            e.target.value = "";
          }}
        />
      </div>

      <div className="fx-co-logo fx-co-logo--head">
        <div className="fx-co-letter" aria-hidden={!hasHead}>
          {hasHead ? (
            <img
              src={companyLetterheadUrl(company)}
              alt=""
              onError={() => setHeadBroken(true)}
            />
          ) : (
            <span>En-tête</span>
          )}
        </div>
        <div>
          <strong>En-tête devis / facture</strong>
          <em>Libre · cadre 240 × 80 px · JPEG, PNG ou WebP · 1,5 Mo</em>
          <div className="fx-co-logo__acts">
            <button type="button" disabled={busy || !owner} onClick={() => headRef.current?.click()}>
              Téléverser
            </button>
            <button type="button" disabled={busy || !owner} onClick={() => setCoffreKind("letterhead")}>
              Depuis le Coffre
            </button>
            {hasHead ? (
              <button type="button" disabled={busy || !owner} onClick={() => setPending("letterhead")}>
                Retirer
              </button>
            ) : null}
          </div>
        </div>
        <input
          ref={headRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => {
            void upload("letterhead", e.target.files?.[0]).catch(() => undefined);
            e.target.value = "";
          }}
        />
      </div>

      <ConfirmModal
        open={pending !== null}
        title={pending === "letterhead" ? "Retirer l’en-tête ?" : "Retirer le logo de profil ?"}
        message="Le fichier est effacé du disque."
        confirmLabel="Retirer"
        busyLabel="Retrait…"
        busy={busy}
        onConfirm={() => {
          if (pending) void remove(pending);
        }}
        onCancel={() => {
          if (!busy) setPending(null);
        }}
      />
      <CoffreLogoPicker
        open={coffreKind !== null}
        busy={busy}
        onClose={() => setCoffreKind(null)}
        onPick={(file) => {
          const kind = coffreKind ?? "mark";
          setCoffreKind(null);
          return upload(kind, file);
        }}
      />
    </div>
  );
}
